import {
  ArchiveOutlined as ArchiveIcon,
  ChevronLeft as ChevronLeftIcon,
  Close as CloseIcon,
  EditOutlined as NewMessageIcon,
  FolderOutlined as ArchivedViewIcon,
  Search as SearchIcon,
  UnarchiveOutlined as UnarchiveIcon,
} from '@mui/icons-material';
import { Box, CircularProgress, IconButton, InputBase, Tooltip, Typography } from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';

import { Dialog } from '@/components/common/Dialog';
import { UserAvatar } from '@/components/common/UserAvatar';
import { ConversationListEntry, useConversationList } from '@/hooks/chat/useConversationList';
import { useClientGroups, useSetGroupInboxState } from '@/hooks/useClientGroups';
import { useUserRole } from '@/hooks/useUserRole';

export interface InboxListProps {
  onSelectGroup: (groupId: string) => void;
}

// Matches the Tooltip styling already used for the icon CTAs in AdvisorHeader.
const tooltipSlotProps = {
  tooltip: { sx: { mb: '0px !important' } },
  popper: { modifiers: [{ name: 'flip', options: { fallbackPlacements: [] } }] },
};

interface ClientPickerEntry {
  groupId: string;
  name: string;
  email?: string;
}

const InboxRow = ({
  convo,
  onSelect,
  onRequestArchiveToggle,
}: {
  convo: ConversationListEntry;
  onSelect: () => void;
  onRequestArchiveToggle: () => void;
}) => {
  const [hovered, setHovered] = useState(false);

  return (
    <Box
      data-testid={`inbox-list-row-${convo.groupId}`}
      onClick={onSelect}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        px: '12px',
        py: '10px',
        cursor: 'pointer',
        borderRadius: '10px',
        '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' },
      }}>
      <UserAvatar name={convo.name} photoUrl={convo.photoUrl} size={38} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: convo.unread > 0 ? 600 : 500,
              color: 'text.primary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
            {convo.name}
          </Typography>
          <Typography sx={{ fontSize: 11, color: 'text.secondary2', flexShrink: 0 }}>
            {convo.lastMsgAt ? formatDistanceToNow(convo.lastMsgAt, { addSuffix: true }) : ''}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mt: '2px' }}>
          <Typography
            sx={{
              flex: 1,
              fontSize: 12,
              color: 'text.secondary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
            {convo.preview}
          </Typography>
          {convo.unread > 0 && (
            <Box
              data-testid={`inbox-list-unread-dot-${convo.groupId}`}
              sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#c0ff02', flexShrink: 0 }}
            />
          )}
        </Box>
      </Box>
      <Tooltip
        title={convo.isInboxArchived ? 'Unarchive' : 'Archive'}
        placement="top"
        slotProps={tooltipSlotProps}>
        <IconButton
          data-testid={`inbox-list-archive-btn-${convo.groupId}`}
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onRequestArchiveToggle();
          }}
          sx={{
            width: 28,
            height: 28,
            flexShrink: 0,
            color: 'text.secondary',
            opacity: hovered ? 1 : 0,
            transition: 'opacity 0.15s',
            '&:hover': { color: 'text.primary', backgroundColor: 'rgba(255,255,255,0.08)' },
          }}>
          {convo.isInboxArchived ? (
            <UnarchiveIcon sx={{ fontSize: 16 }} />
          ) : (
            <ArchiveIcon sx={{ fontSize: 16 }} />
          )}
        </IconButton>
      </Tooltip>
    </Box>
  );
};

// Compose-mode row — just enough to identify and jump to a client, no
// unread/preview/archive affordances (those apply to existing threads,
// not to "who do I want to message").
const ClientPickerRow = ({ client, onSelect }: { client: ClientPickerEntry; onSelect: () => void }) => (
  <Box
    data-testid={`inbox-list-picker-row-${client.groupId}`}
    onClick={onSelect}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      px: '12px',
      py: '10px',
      cursor: 'pointer',
      borderRadius: '10px',
      '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' },
    }}>
    <UserAvatar name={client.name} size={38} />
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: 14,
          fontWeight: 500,
          color: 'text.primary',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
        {client.name}
      </Typography>
      {client.email && (
        <Typography
          sx={{
            fontSize: 12,
            color: 'text.secondary',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
          {client.email}
        </Typography>
      )}
    </Box>
  </Box>
);

export const InboxList = ({ onSelectGroup }: InboxListProps) => {
  const [tab, setTab] = useState<'Open' | 'Archived'>('Open');
  const [search, setSearch] = useState('');
  const [composeMode, setComposeMode] = useState(false);
  const { isAdvisor } = useUserRole();
  const [pendingArchiveToggle, setPendingArchiveToggle] = useState<ConversationListEntry | null>(
    null,
  );
  const { convos: allConvos, isLoadingGroups, isLoadingUnreads } = useConversationList(tab);
  const convos = allConvos.filter((c) => c.name.toLowerCase().includes(search.toLowerCase()));
  const { mutate: setInboxState } = useSetGroupInboxState();

  // Compose mode searches every active client group regardless of tab/archive
  // state, mirroring ChatsPage's old allClients/handlePickClient picker —
  // but takes over the same search bar + list instead of a floating popover.
  const { groups: allGroups } = useClientGroups();
  const allClients: ClientPickerEntry[] = useMemo(
    () =>
      allGroups.map((g) => {
        const primaryMember =
          g.members.find((m) => m.role === 'Buyer' || m.role === 'Primary Owner') ?? g.members[0];
        return {
          groupId: g.id,
          name: isAdvisor ? g.holdingName || primaryMember?.name || 'Client' : g.holdingName || 'Advisor',
          email: isAdvisor ? primaryMember?.email : undefined,
        };
      }),
    [allGroups, isAdvisor],
  );
  const filteredClients = allClients.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      (c.email || '').toLowerCase().includes(search.toLowerCase()),
  );

  const toggleComposeMode = () => {
    setComposeMode((c) => !c);
    setSearch('');
  };

  const exitComposeMode = useCallback(() => {
    setComposeMode(false);
    setSearch('');
  }, []);

  const handleComposeSelect = useCallback(
    (groupId: string) => {
      const group = allGroups.find((g) => g.id === groupId);
      if (group?.isInboxArchived) {
        setInboxState({ groupId, isInboxArchived: false });
      }
      onSelectGroup(groupId);
      exitComposeMode();
    },
    [allGroups, exitComposeMode, onSelectGroup, setInboxState],
  );

  const confirmArchiveToggle = () => {
    if (!pendingArchiveToggle) return;
    setInboxState({
      groupId: pendingArchiveToggle.groupId,
      isInboxArchived: !pendingArchiveToggle.isInboxArchived,
    });
    setPendingArchiveToggle(null);
  };

  return (
    <Box data-testid="inbox-list" sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', px: '20px', pb: '12px', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              width: '200px',
              flexShrink: 0,
              height: '35px',
              backgroundColor: 'var(--tx-fill)',
              border: 0,
              borderRadius: '8px',
              px: '10px',
            }}>
            <SearchIcon sx={{ fontSize: 14, color: 'text.secondary2' }} />
            <InputBase
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={
                composeMode
                  ? isAdvisor
                    ? 'Message a client…'
                    : 'Message an advisor…'
                  : isAdvisor
                  ? 'Search clients…'
                  : 'Search advisors…'
              }
              inputProps={{ 'data-testid': 'inbox-list-search-input' }}
              sx={{
                flex: 1,
                minWidth: 0,
                color: 'text.primary',
                fontSize: 14,
                '& input': { fontSize: 14 },
                '& input::placeholder': {
                  color: 'var(--tx-faint)',
                  opacity: 1,
                  fontSize: 14,
                },
              }}
            />
          </Box>

          {!composeMode &&
            (tab === 'Open' ? (
              <Tooltip title="Archived" placement="top" slotProps={tooltipSlotProps}>
                <IconButton
                  data-testid="inbox-list-view-archived"
                  size="small"
                  onClick={() => setTab('Archived')}
                  sx={{
                    width: 35,
                    height: 35,
                    flexShrink: 0,
                    color: 'text.secondary',
                    border: 0,
                    backgroundColor: 'var(--tx-fill)',
                    borderRadius: '8px',
                    '&:hover': { color: 'text.primary', backgroundColor: 'var(--tx-fill-2)' },
                  }}>
                  <ArchivedViewIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title="Back to Open" placement="top" slotProps={tooltipSlotProps}>
                <IconButton
                  data-testid="inbox-list-view-open"
                  size="small"
                  onClick={() => setTab('Open')}
                  sx={{
                    width: 35,
                    height: 35,
                    flexShrink: 0,
                    color: 'text.secondary',
                    border: 0,
                    backgroundColor: 'var(--tx-fill)',
                    borderRadius: '8px',
                    '&:hover': { color: 'text.primary', backgroundColor: 'var(--tx-fill-2)' },
                  }}>
                  <ChevronLeftIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            ))}
        </Box>

        <Tooltip title={composeMode ? 'Cancel' : 'New message'} placement="top" slotProps={tooltipSlotProps}>
          <IconButton
            data-testid="inbox-list-new-message-btn"
            size="small"
            onClick={toggleComposeMode}
            sx={{
              width: 35,
              height: 35,
              flexShrink: 0,
              color: '#262626',
              backgroundColor: 'var(--tx-fill-2)',
              border: 'none',
              borderRadius: '8px',
              '&:hover': { backgroundColor: '#C8C4BD' },
            }}>
            {composeMode ? <CloseIcon sx={{ fontSize: 17 }} /> : <NewMessageIcon sx={{ fontSize: 15 }} />}
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: '20px', pb: '20px' }}>
        {composeMode ? (
          filteredClients.length === 0 ? (
            <Typography
              data-testid="inbox-list-empty-state"
              sx={{ textAlign: 'center', fontSize: 13, color: 'text.secondary2', pt: '40px' }}>
              {isAdvisor ? 'No clients found' : 'No advisors found'}
            </Typography>
          ) : (
            filteredClients.map((client) => (
              <ClientPickerRow
                key={client.groupId}
                client={client}
                onSelect={() => handleComposeSelect(client.groupId)}
              />
            ))
          )
        ) : isLoadingGroups || isLoadingUnreads ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: '40px' }}>
            <CircularProgress size={22} sx={{ color: '#c0ff02' }} />
          </Box>
        ) : convos.length === 0 ? (
          <Typography
            data-testid="inbox-list-empty-state"
            sx={{ textAlign: 'center', fontSize: 13, color: 'text.secondary2', pt: '40px' }}>
            {search ? 'No matches' : 'No conversations'}
          </Typography>
        ) : (
          convos.map((convo) => (
            <InboxRow
              key={convo.groupId}
              convo={convo}
              onSelect={() => onSelectGroup(convo.groupId)}
              onRequestArchiveToggle={() => setPendingArchiveToggle(convo)}
            />
          ))
        )}
      </Box>

      <Dialog
        open={Boolean(pendingArchiveToggle)}
        onClose={() => setPendingArchiveToggle(null)}
        width={360}
        containerProps={{ gap: 0, p: 0 }}>
        <div style={{ padding: 24 }}>
          <div style={{ fontSize: 17, fontWeight: 600, color: 'var(--tx-text)', fontFamily: "'Manrope', sans-serif", marginBottom: 6 }}>
            {pendingArchiveToggle?.isInboxArchived ? 'Unarchive conversation?' : 'Archive conversation?'}
          </div>
          <div style={{ fontSize: 13, color: 'var(--tx-muted)', fontFamily: "'Manrope', sans-serif", lineHeight: 1.6, marginBottom: 20 }}>
            {pendingArchiveToggle?.isInboxArchived
              ? `${pendingArchiveToggle?.name} will move back to Open.`
              : `${pendingArchiveToggle?.name} will move to Archived. You can unarchive it any time.`}
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Box
              onClick={() => setPendingArchiveToggle(null)}
              sx={{
                px: '14px',
                py: '8px',
                borderRadius: '8px',
                fontSize: 13,
                fontWeight: 600,
                color: 'text.secondary',
                cursor: 'pointer',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
              }}>
              Cancel
            </Box>
            <Box
              data-testid="inbox-list-confirm-archive-toggle"
              onClick={confirmArchiveToggle}
              sx={{
                px: '14px',
                py: '8px',
                borderRadius: '8px',
                fontSize: 13,
                fontWeight: 600,
                color: '#1A1A1A',
                backgroundColor: 'var(--tx-lime)',
                cursor: 'pointer',
                '&:hover': { backgroundColor: '#D4FF4D' },
              }}>
              {pendingArchiveToggle?.isInboxArchived ? 'Unarchive' : 'Archive'}
            </Box>
          </div>
        </div>
      </Dialog>
    </Box>
  );
};

export default InboxList;
